import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const ModernAuthenticator = ({ children }) => {
  const { user, isLoading, signIn, error, clearError } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  // Redirect to /admin after successful login
  useEffect(() => {
    if (user && window.location.pathname === '/') {
      console.log('🔄 User logged in, redirecting to /admin');
      window.location.href = '/admin';
    }
  }, [user]);

  // Show children if user is authenticated
  if (user) {
    return children;
  }

  // Show loading during auth check
  if (isLoading) {
    return (
      <div className="auth-loading">
        <div className="loading-content">
          <div className="spinner"></div>
          <h3>Initializing...</h3>
          <p>Setting up your link management dashboard</p>
        </div>

        <style jsx>{`
          .auth-loading {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          }

          .loading-content {
            text-align: center;
            color: white;
          }

          .spinner {
            width: 48px;
            height: 48px;
            border: 4px solid rgba(255,255,255,0.3);
            border-top: 4px solid white;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 20px;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) clearError();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await signIn(formData.email, formData.password);
    } catch (err) {
      console.error('Login error:', err);
    }
  };

  return (
    <div className="modern-auth">
      <div className="auth-container">
        {/* Left Side - Branding */}
        <div className="auth-branding">
          <div className="brand-content">
            <div className="brand-logo">
              <div className="logo-icon">🔗</div>
              <h1>ShortLink</h1>
            </div>

            <div className="brand-description">
              <h2>Powerful URL Management</h2>
              <p>Create, track, and optimize your short links with advanced analytics and custom domains.</p>

              <div className="features-list">
                <div className="feature-item">
                  <span className="feature-icon">⚡</span>
                  <span>Lightning-fast link creation</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">📊</span>
                  <span>Real-time analytics & insights</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">🌐</span>
                  <span>Custom domain management</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">🔒</span>
                  <span>Enterprise-grade security</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="auth-form-section">
          <div className="form-container">
            <div className="form-header">
              <h3>Welcome back</h3>
              <p>Sign in to access your link management dashboard</p>
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label className="form-label">Email address</label>
                <div className="input-wrapper">
                  <span className="input-icon">📧</span>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter your email"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <div className="input-wrapper">
                  <span className="input-icon">🔒</span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="btn-spinner"></div>
                    Signing in...
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <span className="btn-arrow">→</span>
                  </>
                )}
              </button>
            </form>

          </div>
        </div>
      </div>

      <style jsx>{`
        .modern-auth {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .auth-container {
          background: white;
          border-radius: 24px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          overflow: hidden;
          display: grid;
          grid-template-columns: 1fr 1fr;
          max-width: 1000px;
          width: 100%;
          min-height: 600px;
        }

        .auth-branding {
          background: linear-gradient(135deg, #1e3a8a 0%, #3730a3 100%);
          color: white;
          padding: 60px 40px;
          display: flex;
          align-items: center;
          position: relative;
          overflow: hidden;
        }

        .auth-branding::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px);
          background-size: 30px 30px;
          animation: float 20s linear infinite;
        }

        @keyframes float {
          0% { transform: translate(0, 0); }
          100% { transform: translate(-30px, -30px); }
        }

        .brand-content {
          position: relative;
          z-index: 1;
        }

        .brand-logo {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 40px;
        }

        .logo-icon {
          font-size: 48px;
          background: rgba(255,255,255,0.2);
          border-radius: 16px;
          width: 72px;
          height: 72px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .brand-logo h1 {
          margin: 0;
          font-size: 32px;
          font-weight: 700;
        }

        .brand-description h2 {
          margin: 0 0 16px 0;
          font-size: 28px;
          font-weight: 600;
          line-height: 1.2;
        }

        .brand-description p {
          margin: 0 0 32px 0;
          font-size: 16px;
          line-height: 1.5;
          opacity: 0.9;
        }

        .features-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 16px;
        }

        .feature-icon {
          font-size: 20px;
          width: 32px;
          height: 32px;
          background: rgba(255,255,255,0.2);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .auth-form-section {
          padding: 60px 40px;
          display: flex;
          align-items: center;
        }

        .form-container {
          width: 100%;
          max-width: 400px;
          margin: 0 auto;
        }

        .form-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .form-header h3 {
          margin: 0 0 8px 0;
          font-size: 28px;
          color: #1a1a1a;
          font-weight: 700;
        }

        .form-header p {
          margin: 0;
          color: #6b7280;
          font-size: 16px;
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #fee2e2;
          color: #dc2626;
          padding: 12px 16px;
          border-radius: 12px;
          margin-bottom: 24px;
          font-size: 14px;
          border: 1px solid #fecaca;
        }

        .auth-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-label {
          font-weight: 600;
          color: #374151;
          font-size: 14px;
        }

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 14px;
          font-size: 16px;
          color: #6b7280;
          z-index: 1;
        }

        .form-input {
          width: 100%;
          padding: 14px 14px 14px 44px;
          border: 1px solid #d1d5db;
          border-radius: 12px;
          font-size: 16px;
          background: #f9fafb;
          transition: all 0.3s ease;
        }

        .form-input:focus {
          outline: none;
          border-color: #667eea;
          background: white;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .toggle-password {
          position: absolute;
          right: 14px;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 16px;
          color: #6b7280;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s ease;
        }

        .toggle-password:hover {
          background: #f3f4f6;
        }

        .submit-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 16px 24px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 8px;
          position: relative;
          overflow: hidden;
        }

        .submit-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.5s ease;
        }

        .submit-btn:hover:not(:disabled)::before {
          left: 100%;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .btn-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .btn-arrow {
          font-size: 18px;
          transition: transform 0.3s ease;
        }

        .submit-btn:hover .btn-arrow {
          transform: translateX(4px);
        }


        @media (max-width: 768px) {
          .auth-container {
            grid-template-columns: 1fr;
            margin: 10px;
            max-width: none;
            min-height: auto;
          }

          .auth-branding {
            padding: 40px 30px;
            text-align: center;
          }

          .brand-description h2 {
            font-size: 24px;
          }

          .auth-form-section {
            padding: 40px 30px;
          }

          .features-list {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }

          .feature-item {
            font-size: 14px;
          }
        }

        @media (max-width: 480px) {
          .modern-auth {
            padding: 10px;
          }

          .auth-branding,
          .auth-form-section {
            padding: 30px 20px;
          }

          .features-list {
            grid-template-columns: 1fr;
          }

          .form-input {
            font-size: 16px; /* Prevent zoom on iOS */
          }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ModernAuthenticator;